import moment from 'moment';
import { isDateDue } from '../../helpers/commonHelper';
import { TaskDetails } from '../../leadsDetail/LeadsDetailRepository';
import { EnquiryDetail } from '../models/Enquiry';
import { getActivityCategory, ActivityCategory } from './HistoryActivity';

// Shared derivation for the enquiry header's two Journey widgets (EnquiryJourneyStatsWidget -
// the 4 count cards, EnquiryJourneyTimelineWidget - the stage timeline). Split into its own
// data-only module (no JSX) so both widgets build their stats/stages from one place rather than
// duplicating the categorisation/date logic, while each still renders independently and memoises
// independently. Reuses exactly "the same query & data from the activity tab and enquiry data"
// (leadDetailsHistory's own task list, already fetched for the Activity tab; enquiryDetail's own
// fields) - no new query. getActivityCategory (HistoryActivity.tsx) does the same
// calls/email/meeting/notes grouping the Activity tab's own filter chips use, so "a follow-up"
// here means the same thing it means there.
export const JOURNEY_FOLLOW_UP_CATEGORIES: ActivityCategory[] = ['calls', 'emailsAndSms', 'meetings', 'notes'];
// Beyond this many completed follow-ups the individual stage nodes collapse into one summary
// pill (mirrors Journey.png's own "if we have a lot of follow-ups" state) - a real enquiry can
// easily rack up dozens of calls/emails over months, and a timeline with 20 circles in a row
// would overflow this gap and defeat the point of an at-a-glance widget.
export const JOURNEY_EXPANDED_STAGE_LIMIT = 4;
// Caps how many individual dots the collapsed pill draws for a very large follow-up count -
// past this it's not adding information, just width, so the count text alone covers the rest.
export const JOURNEY_COLLAPSED_DOT_LIMIT = 16;

// Explicit request: a lighter orange for every follow-up element than the app's own
// --rv-accent-orange (#E05A00, a dark burnt orange that read as reddish at small/bold sizes -
// see the earlier "no red" fix). Scoped to these two widgets rather than changing the shared
// CSS variable, which other, unrelated parts of the app still use for their own meaning.
export const JOURNEY_FOLLOW_UP_COLOR = '#ff964f';
export const JOURNEY_FOLLOW_UP_COLOR_BG = 'rgba(255, 150, 79, 0.16)';

// Explicit correction (reference: headerWidget.png/Assignment.png's own red "overdue" state) -
// supersedes this file's own earlier "no red for overdue" rule: the new Next Follow-up header
// widget (EnquiryNextFollowUpWidget) now uses red specifically for "overdue", so this stage
// timeline's own due/overdue node follows the same colour for the same meaning instead of
// disagreeing with it. Completed follow-ups themselves move to 'blue' (a plain count, not an
// urgency signal - explicit request not to use orange there any more either), leaving 'orange'
// free to mean "due today" consistently across both header widgets.
export type JourneyTone = 'blue' | 'orange' | 'red' | 'green' | 'grey';

// Explicit bug report: Outcome's value is a word ("Closed"/"Matter"), not a number like the
// other 3 tiles - at the same large font-size requested for the numbers, a word that wide
// overflowed its tile's background. kind lets the widget size this one down without touching
// the 3 genuinely numeric tiles.
export interface JourneyStat {
    label: string;
    value: string;
    tone: JourneyTone;
    kind: 'number' | 'text';
}

export type JourneyStageIcon = 'received' | 'number' | 'due' | 'overdue' | 'matter' | 'flag';

export interface JourneyNodeStage {
    type: 'node';
    key: string;
    iconKind: JourneyStageIcon;
    numberValue?: number;
    label: string;
    date: string;
    tone: JourneyTone;
    dashed?: boolean;
}

export interface JourneyCollapsedStage {
    type: 'collapsed';
    key: string;
    count: number;
    lastDoneLabel: string;
}

export type JourneyStage = JourneyNodeStage | JourneyCollapsedStage;

export interface JourneyData {
    stats: JourneyStat[];
    stages: JourneyStage[];
}

// Explicit request: also used by EnquiryFormDrawer.tsx's own header chip (now "Open for N
// days", replacing its old due/overdue duplicate of the new Next Follow-up widget below) - one
// shared formula rather than two copies of the same diff.
export const getDaysOpen = (enquiryDetail: EnquiryDetail): number =>
    Math.max(0, moment().diff(moment(enquiryDetail.createdDate), 'days'));

export const buildJourneyData = (enquiryDetail: EnquiryDetail, tasks: TaskDetails[]): JourneyData => {
    const completedFollowUps = tasks
        .filter(task => JOURNEY_FOLLOW_UP_CATEGORIES.includes(getActivityCategory(task)) && !!task.completedDate)
        // tslint:disable-next-line: no-non-null-assertion
        .sort((a, b) => new Date(a.completedDate!).getTime() - new Date(b.completedDate!).getTime());

    const daysOpen = getDaysOpen(enquiryDetail);
    const isMatterCreated = !!enquiryDetail.matterGuid;
    const isNotProceeding = !isMatterCreated && enquiryDetail.outcome === 'Not Proceeding';
    const isOutcomePending = !isMatterCreated && !isNotProceeding;
    const isOverdue = isOutcomePending && !!enquiryDetail.followUpDueDate && isDateDue(enquiryDetail.followUpDueDate);
    const hasPendingFollowUp = isOutcomePending && !!enquiryDetail.followUpDueDate;
    // Explicit bug report: the Due step only ever distinguished overdue (red) vs not (blue) -
    // missing the "due today" amber state EnquiryNextFollowUpWidget.tsx already shows for the
    // exact same date. Mirrors that widget's own isDueToday computation.
    const isDueToday = hasPendingFollowUp && !isOverdue && !!enquiryDetail.followUpDueDate
        && moment(enquiryDetail.followUpDueDate).isSame(moment(), 'day');

    const stats: JourneyStat[] = [
        { label: 'Days open', value: String(daysOpen), tone: 'blue', kind: 'number' },
        { label: 'Follow-ups', value: String(completedFollowUps.length), tone: 'orange', kind: 'number' },
        { label: 'Overdue', value: isOverdue ? '1' : '0', tone: isOverdue ? 'orange' : 'grey', kind: 'number' },
        {
            label: 'Outcome',
            value: isMatterCreated ? 'Matter' : (isNotProceeding ? 'Closed' : '—'),
            tone: isMatterCreated ? 'green' : 'grey',
            kind: 'text',
        },
    ];

    const stages: JourneyStage[] = [
        {
            type: 'node',
            key: 'received',
            // Explicit request: same icon/colour as the Activity tab's own "Created" entry
            // (HistoryActivity.tsx's enquiryCreated(): CheckOutlinedIcon, green) - this node
            // represents the same event, so it should look like it.
            iconKind: 'received',
            label: 'Received',
            date: moment(enquiryDetail.createdDate).format('D MMM'),
            tone: 'green',
        },
    ];

    // Explicit bug report: the collapsed (>4 follow-ups) box used to carry the Due step's own
    // "next"/overdue date itself, duplicating both the follow-up count (already on
    // EnquiryNextFollowUpWidget's own badge) and the overdue date (already on that same widget)
    // inside a box that was also still blue-tinted while showing red overdue text. Due is now
    // always its own step below, pushed once after this if/else regardless of whether the
    // collapsed box or the individual follow-up nodes rendered - "exactly as in the 4-or-fewer
    // layout" means both cases share the exact same Due step, not two different renderings of
    // the same fact.
    const showCollapsed = completedFollowUps.length > JOURNEY_EXPANDED_STAGE_LIMIT;
    if (showCollapsed) {
        const lastFollowUp = completedFollowUps[completedFollowUps.length - 1];
        stages.push({
            type: 'collapsed',
            key: 'follow-ups',
            count: completedFollowUps.length,
            // tslint:disable-next-line: no-non-null-assertion
            lastDoneLabel: moment(lastFollowUp.completedDate!).format('D MMM'),
        });
    } else {
        completedFollowUps.forEach((task, index) => {
            stages.push({
                type: 'node',
                key: task.taskGuidID,
                iconKind: 'number',
                numberValue: index + 1,
                // Explicit request: show the actual follow-up action text (client-configurable,
                // e.g. a firm's own custom action names) instead of a hardcoded label - same
                // actionedDescription-then-followUpAction precedence HistoryActivity.tsx's own
                // getDescription() already uses to title these same tasks' Activity cards.
                //
                // Explicit bug report: a system-generated MeetingReminder task has neither
                // actionedDescription nor followUpAction populated, leaving this step's label
                // blank - same taskTypeInternalDescription check, and the same fallback text,
                // HistoryActivity.tsx's own getDescription() now also uses for the same task type.
                label: (task.actionedDescription && task.actionedDescription.length > 0)
                    ? task.actionedDescription
                    : task.followUpAction || (task.taskTypeInternalDescription === 'MeetingReminder' ? 'Meeting Reminder' : task.followUpAction),
                // tslint:disable-next-line: no-non-null-assertion
                date: moment(task.completedDate!).format('D MMM'),
                // Explicit request: a completed follow-up is a plain count, not an urgency
                // signal - blue instead of the old orange (which now means "due today" on the
                // Next Follow-up header widget instead).
                tone: 'blue',
            });
        });
    }

    if (hasPendingFollowUp && enquiryDetail.followUpDueDate) {
        const dueDateLabel = moment(enquiryDetail.followUpDueDate).format('D MMM');
        stages.push({
            type: 'node',
            key: 'due',
            iconKind: isOverdue ? 'overdue' : 'due',
            // Explicit correction: back to a 3-way label swap (Due / Due today / Overdue) - the
            // date line stays a plain date always, no "· overdue"/"· due today" suffix, so it
            // never risks overflowing its fixed-width column.
            label: isOverdue ? 'Overdue' : isDueToday ? 'Due today' : 'Due',
            date: dueDateLabel,
            // Explicit correction: red for overdue, amber for due today (matching the new Next
            // Follow-up header widget's own 3-tone state for the exact same date), blue
            // otherwise - supersedes this node's old 2-tone (red/blue only) scheme.
            tone: isOverdue ? 'red' : isDueToday ? 'orange' : 'blue',
            dashed: true,
        });
    }

    stages.push({
        type: 'node',
        key: 'outcome',
        iconKind: isMatterCreated ? 'matter' : 'flag',
        label: isMatterCreated ? 'Matter created' : (isNotProceeding ? 'Not proceeding' : 'Outcome pending'),
        date: isMatterCreated && enquiryDetail.matterTitle ? enquiryDetail.matterTitle : '',
        tone: isMatterCreated ? 'green' : 'grey',
        dashed: isOutcomePending,
    });

    return { stats, stages };
};
